import React, { useState, useMemo, useEffect } from "react";
import {
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Chip,
  Box,
} from "@mui/material";
import { IFundAccount } from "interfaces/fund-account";
import { useCustom } from "@refinedev/core";
import { IUser } from "interfaces/user";

interface Props {
  fundAccountId: number;
  setSelectedUser: (users: IUser[]) => void;
  selectedAccount: any;
  value?: IUser[];
  showError?: boolean;
  filterEmails?: string[];
  onEmailsChange?: (emails: string[]) => void;
  onFilterEmailsChange?: (emails: string[]) => void;
  // New prop to toggle between "to" and "cc" functionality
  isCC?: boolean;
  // Optional custom labels
  label?: string;
  emptyPlaceholder?: string;
}

const UsersEmailSelection = ({
  fundAccountId,
  setSelectedUser,
  value = [],
  showError = false,
  filterEmails = [],
  onEmailsChange,
  onFilterEmailsChange,
  isCC = false, // Default to "to" functionality
  label, // Custom label
  emptyPlaceholder, // Custom empty placeholder
}: Props) => {
  const [hasError, setHasError] = useState(false);
  const [hasPreSelected, setHasPreSelected] = useState(false);

  const selectedValues = value;

  const selectedEmails = useMemo(() => {
    return selectedValues.map((user) => user.email).filter((email) => email);
  }, [selectedValues]);

  useEffect(() => {
    setHasPreSelected(false);
  }, [filterEmails]);

  useEffect(() => {
    if (onEmailsChange) {
      onEmailsChange(selectedEmails);
    }
  }, [filterEmails, selectedEmails, onEmailsChange]);

  const {
    data: fundAccount,
    isLoading,
    isSuccess,
  } = useCustom<IFundAccount>({
    url: `funds/${fundAccountId}?removeSuperAdmin=true`,
    method: "get",
    queryOptions: {
      enabled: !!fundAccountId,
    },
  });

  const allUsers = useMemo(() => {
    return fundAccount?.data.fundAccountMembers || [];
  }, [fundAccount?.data.fundAccountMembers]);

  useEffect(() => {
    if (
      filterEmails.length > 0 &&
      allUsers.length > 0 &&
      selectedValues.length === 0 &&
      !hasPreSelected
    ) {
      const preSelectedUsers = allUsers
        .filter(
          (member) =>
            member.user.email && filterEmails.includes(member.user.email)
        )
        .map((member) => member.user);

      if (preSelectedUsers.length > 0) {
        setSelectedUser(preSelectedUsers);
      }
      setHasPreSelected(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, hasPreSelected]);

  const handleUserChange = (event: any) => {
    const selectedUsers = event.target.value as IUser[];
    setSelectedUser(selectedUsers);

    if (selectedUsers.length > 0) {
      setHasError(false);
    }
  };

  const shouldShowError =
    (showError || hasError) && selectedValues.length === 0;

  const hasNoUsers = !isLoading && (!allUsers || allUsers.length === 0);

  // Dynamic text based on isCC flag
  const getTitleText = () => {
    if (label) return label;
    return isCC
      ? "Please, Choose the users to CC:"
      : "Please, Choose the users to notify:";
  };

  const getEmptyPlaceholder = () => {
    if (emptyPlaceholder) return emptyPlaceholder;
    return "Select Users";
  };

  const getErrorText = () => {
    return isCC
      ? "Please select at least one user to CC"
      : "Please select at least one user to proceed";
  };

  const getEmailSectionTitle = () => {
    return isCC
      ? `CC Emails (${Array.from(new Set([...selectedEmails])).length})`
      : `Emails to notify (${Array.from(new Set([...selectedEmails])).length})`;
  };

  return (
    <Grid item xs={12} sm={12} sx={{ px: 1, pb: 1 }}>
      <Typography variant="bodyCopyRegular">
        {getTitleText()}
      </Typography>

      <FormControl fullWidth sx={{ mt: 1 }} error={shouldShowError}>
        <Select
          multiple
          fullWidth
          name="users"
          size="small"
          value={selectedValues}
          displayEmpty
          onChange={handleUserChange}
          disabled={isLoading || hasNoUsers}
          startAdornment={
            isLoading ? (
              <CircularProgress
                size={16}
                color="inherit"
                sx={{ ml: 1, mr: 1 }}
              />
            ) : null
          }
          renderValue={(selected) => {
            if (isLoading) {
              return <span style={{ color: "#999" }}>Loading users...</span>;
            }
            if (selected.length === 0) {
              return <em style={{ color: "#999" }}>{getEmptyPlaceholder()}</em>;
            }
            return (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((user) => (
                  <Chip
                    key={`${user.firstName}-${user.lastName}-${user.userId || Math.random()
                      }`}
                    label={`${user.firstName} ${user.lastName}`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            );
          }}
        >
          {isLoading ? (
            <MenuItem disabled>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "16px",
                }}
              >
                <CircularProgress size={20} color="primary" />
                <span style={{ marginLeft: "8px", color: "#666" }}>
                  Loading users...
                </span>
              </div>
            </MenuItem>
          ) : isSuccess && hasNoUsers ? (
            <MenuItem disabled>
              <span style={{ color: "#999", fontStyle: "italic" }}>
                No users available
              </span>
            </MenuItem>
          ) : (
            allUsers?.map((member, index) => (
              <MenuItem key={index} value={member.user as any}>
                {member.user.firstName} {member.user.lastName}
                {member.user.email && (
                  <span
                    style={{
                      color: "#666",
                      marginLeft: "8px",
                      fontSize: "0.9em",
                    }}
                  >
                    ({member.user.email})
                  </span>
                )}
              </MenuItem>
            ))
          )}
        </Select>

        {shouldShowError && (
          <FormHelperText>
            {getErrorText()}
          </FormHelperText>
        )}

        {!isLoading && hasNoUsers && isSuccess && (
          <FormHelperText>No users found for this fund account</FormHelperText>
        )}
      </FormControl>

      {/* Unified Email Display */}
      {(filterEmails.length > 0 || selectedEmails.length > 0) && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="bodyCopyBold" sx={{ pb: 2 }}>
            {getEmailSectionTitle()}
          </Typography>
          {Array.from(new Set([...selectedEmails])).map(
            (email, index, allEmails) => {
              return (
                <Box
                  key={`${email}-${index}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    my: 1,
                    ...(index === allEmails.length - 1 && { mb: 0 }),
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="bodyCopyRegular"
                      color="text.secondary"
                    >
                      • {email}
                    </Typography>
                  </Box>
                </Box>
              );
            }
          )}
        </Box>
      )}
    </Grid>
  );
};

export default UsersEmailSelection;
